import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Button,
  Typography,
  Spin,
  Divider,
  Space,
  Form,
  Input,
  Modal,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useToast } from '../../hooks/toast';
import api from '../../services/api';
import { ContainerConfirmSchedule, ScheduleDetailsCard } from './styles';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useI18n } from '../../shared/i18n';

dayjs.extend(utc);

interface IScheduleDetails {
  id: string;
  client_name: string;
  professional_name: string;
  start: string;
  end: string;
  description?: string;
  observation?: string;
  client_email?: string;
  client_phone?: string;
  client_address?: string;
  status?: string;
  company_name?: string;
}

const { Title, Text, Paragraph } = Typography;

const ScheduleConfirm: React.FC = () => {
  const { token, schedule_id, tenant } = useParams() as any;
  const { addToast } = useToast();
  const { t, language } = useI18n();

  const [loading, setLoading] = useState<boolean>(true);
  const [loadingConfirm, setLoadingConfirm] = useState<boolean>(false);
  const [schedule, setSchedule] = useState<IScheduleDetails | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [confirmedAt, setConfirmedAt] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string>('confirmed');
  const [notes, setNotes] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  const formatScheduleDate = useCallback(
    (date: string) =>
      new Intl.DateTimeFormat(language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(dayjs.utc(date).toDate()),
    [language],
  );

  const loadScheduleData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/schedules/confirm/${schedule_id}?token=${token}`,
      );

      if (response.data?.data) {
        setSchedule(response.data.data);
        setStatus('success');
        setStatusMessage(t('scheduleConfirm.error.foundSuccess'));
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || t('scheduleConfirm.error.invalidLink');
      setStatus('error');
      setStatusMessage(errorMessage);
      addToast({
        type: 'error',
        title: t('scheduleConfirm.error.title'),
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [token, schedule_id, addToast, t]);

  useEffect(() => {
    if (!token || !schedule_id) {
      setStatus('error');
      setStatusMessage(t('scheduleConfirm.error.missingParams'));
      setLoading(false);
      return;
    }

    if (tenant) {
      try {
        sessionStorage.setItem('@Sigeclin:clinic', tenant);
        api.defaults.headers['x-tenant-id'] = tenant;
      } catch (e) {
        // ignore
      }
    }

    loadScheduleData();
  }, [token, schedule_id, loadScheduleData, t]);

  const handleSubmitConfirmation = useCallback(async () => {
    if (!schedule) return;

    try {
      setLoadingConfirm(true);
      const response = await api.post(`/schedules/confirm/${schedule.id}`, {
        token,
        status: confirmation,
        notes: notes || undefined,
      });

      if (response.data?.success) {
        setStatus('confirmed');
        setStatusMessage(
          confirmation === 'confirmed'
            ? t('scheduleConfirm.confirmation.confirmedSuccess')
            : t('scheduleConfirm.confirmation.cancelledSuccess'),
        );
        setConfirmedAt(new Date().toISOString());
        addToast({
          type: 'success',
          title: t('scheduleConfirm.confirmation.successTitle'),
          description:
            confirmation === 'confirmed'
              ? t('scheduleConfirm.confirmation.confirmedToast')
              : t('scheduleConfirm.confirmation.cancelledToast'),
        });
        setShowConfirmModal(false);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        t('scheduleConfirm.error.processConfirmation');
      addToast({
        type: 'error',
        title: t('scheduleConfirm.error.title'),
        description: errorMessage,
      });
    } finally {
      setLoadingConfirm(false);
    }
  }, [schedule, token, confirmation, notes, addToast, t]);

  if (loading) {
    return (
      <ContainerConfirmSchedule>
        <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Spin size="large" tip={t('scheduleConfirm.loading')} />
        </Card>
      </ContainerConfirmSchedule>
    );
  }

  if (status === 'error') {
    return (
      <ContainerConfirmSchedule>
        <Card
          style={{
            maxWidth: '600px',
            margin: '0 auto',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: 'none',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#fff1f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <CloseCircleOutlined
              style={{ fontSize: '40px', color: '#ff4d4f' }}
            />
          </div>

          <Title level={2} style={{ color: '#ff4d4f', marginBottom: '8px' }}>
            {t('scheduleConfirm.error.invalidTitle')}
          </Title>

          <Paragraph style={{ color: '#8c8c8c', marginBottom: '24px' }}>
            {statusMessage}
          </Paragraph>
        </Card>
      </ContainerConfirmSchedule>
    );
  }

  if (status === 'confirmed') {
    const isConfirmed = confirmation === 'confirmed';
    return (
      <ContainerConfirmSchedule>
        <Card
          style={{
            maxWidth: '600px',
            margin: '0 auto',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: 'none',
            textAlign: 'center',
            width: '100%',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: isConfirmed ? '#f6ffed' : '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            {isConfirmed ? (
              <CheckCircleOutlined
                style={{ fontSize: '32px', color: '#52c41a' }}
              />
            ) : (
              <CloseCircleOutlined
                style={{ fontSize: '32px', color: '#ff4d4f' }}
              />
            )}
          </div>

          <Title
            level={2}
            style={{
              color: isConfirmed ? '#52c41a' : '#ff4d4f',
              marginBottom: '6px',
              fontSize: '20px',
            }}
          >
            {isConfirmed ? 'Agendamento Confirmado!' : 'Agendamento Cancelado'}
            {isConfirmed
              ? t('scheduleConfirm.confirmation.confirmedCardTitle')
              : t('scheduleConfirm.confirmation.cancelledCardTitle')}
          </Title>

          <Paragraph
            style={{ color: '#8c8c8c', marginBottom: '16px', fontSize: '14px' }}
          >
            {statusMessage}
          </Paragraph>

          {confirmedAt && (
            <Text type="secondary" style={{ fontSize: '13px' }}>
              {t('scheduleConfirm.confirmation.confirmedAt', {
                date: dayjs.utc(confirmedAt).format('DD/MM/YYYY HH:mm'),
              })}
            </Text>
          )}

          <Divider style={{ margin: '16px 0' }} />

          <Button
            type="primary"
            size="large"
            href="/"
            style={{
              borderRadius: '6px',
              height: '36px',
              fontSize: '14px',
            }}
          >
            {t('scheduleConfirm.confirmation.backHome')}
          </Button>
        </Card>
      </ContainerConfirmSchedule>
    );
  }

  if (!schedule) {
    return (
      <ContainerConfirmSchedule>
        <Card
          style={{
            maxWidth: '600px',
            margin: '0 auto',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: 'none',
            textAlign: 'center',
            width: '100%',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <Text style={{ fontSize: '32px' }}>404</Text>
          </div>

          <Title
            level={2}
            style={{ color: '#262626', marginBottom: '6px', fontSize: '20px' }}
          >
            {t('scheduleConfirm.error.notFoundTitle')}
          </Title>

          <Paragraph
            style={{ color: '#8c8c8c', marginBottom: '16px', fontSize: '14px' }}
          >
            {t('scheduleConfirm.error.notFoundDescription')}
          </Paragraph>

          <Divider style={{ margin: '16px 0' }} />

          <Button
            type="primary"
            size="large"
            href="/"
            style={{
              borderRadius: '6px',
              height: '36px',
              fontSize: '14px',
            }}
          >
            {t('scheduleConfirm.confirmation.backHome')}
          </Button>
        </Card>
      </ContainerConfirmSchedule>
    );
  }

  const startTime = dayjs.utc(schedule.start);
  const endTime = dayjs.utc(schedule.end);
  const daysUntil = startTime.diff(dayjs(), 'day');

  return (
    <ContainerConfirmSchedule>
      <Card
        style={{
          maxWidth: '700px',
          margin: '0 auto',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: 'none',
          width: '100%',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '12px',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CalendarOutlined
                style={{ fontSize: '24px', color: '#ffffff' }}
              />
            </div>
          </div>
          <Title level={2} style={{ marginBottom: '6px', fontSize: '20px' }}>
            {t('scheduleConfirm.hero.title')}
          </Title>
          <Text
            type="secondary"
            style={{ fontSize: '13px', lineHeight: '1.5' }}
          >
            {t('scheduleConfirm.hero.description')}
          </Text>
        </div>

        <Divider style={{ margin: '20px 0' }} />

        <ScheduleDetailsCard>
          <div className="detail-section">
            <h3>📅 {t('scheduleConfirm.details.dateTime')}</h3>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>
                <CalendarOutlined />{' '}
                {t('scheduleConfirm.details.fromTo', {
                  date: formatScheduleDate(schedule.start),
                  start: startTime.format('HH:mm'),
                  end: endTime.format('HH:mm'),
                })}
              </Text>
              {daysUntil > 0 && (
                <Text type="secondary">
                  {t(
                    daysUntil === 1
                      ? 'scheduleConfirm.details.daysUntilOne'
                      : 'scheduleConfirm.details.daysUntilOther',
                    { count: daysUntil },
                  )}
                </Text>
              )}
            </Space>
          </div>

          {schedule.company_name && (
            <div className="detail-section">
              <h3>🏥 {t('scheduleConfirm.details.clinic')}</h3>
              <Text>
                <EnvironmentOutlined /> {schedule.company_name}
              </Text>
            </div>
          )}

          <div className="detail-section">
            <h3>👨‍⚕️ {t('scheduleConfirm.details.professional')}</h3>
            <Text>
              <UserOutlined /> {schedule.professional_name}
            </Text>
          </div>

          <div className="detail-section">
            <h3>👤 {t('scheduleConfirm.details.patient')}</h3>
            <Text>
              <UserOutlined /> {schedule.client_name}
            </Text>
          </div>

          {schedule.client_phone && (
            <div className="detail-section">
              <h3>📱 {t('scheduleConfirm.details.phone')}</h3>
              <Text>
                <PhoneOutlined /> {schedule.client_phone}
              </Text>
            </div>
          )}
          {schedule.client_address && (
            <div className="detail-section">
              <h3>📍 {t('scheduleConfirm.details.address')}</h3>
              <Text>
                <EnvironmentOutlined /> {schedule.client_address}
              </Text>
            </div>
          )}

          {schedule.observation && (
            <div className="detail-section">
              <h3>📝 {t('scheduleConfirm.details.observation')}</h3>
              <Paragraph>{schedule.observation}</Paragraph>
            </div>
          )}
        </ScheduleDetailsCard>

        <Divider style={{ margin: '20px 0' }} />

        <div style={{ marginBottom: '24px', marginTop: '24px' }}>
          <Title
            level={4}
            style={{ marginBottom: '12px', color: '#262626', fontSize: '16px' }}
          >
            {t('scheduleConfirm.decision.title')}
          </Title>
          <div
            style={{
              display: 'grid',
              gap: '12px',
              gridTemplateColumns: '1fr 1fr',
            }}
          >
            <div
              onClick={() => setConfirmation('confirmed')}
              style={{
                padding: '12px',
                borderRadius: '6px',
                border:
                  confirmation === 'confirmed'
                    ? '2px solid #52c41a'
                    : '2px solid #f0f0f0',
                backgroundColor:
                  confirmation === 'confirmed' ? '#f6ffed' : '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'center',
              }}
              onMouseEnter={(e) => {
                if (confirmation !== 'confirmed') {
                  e.currentTarget.style.borderColor = '#d9d9d9';
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }
              }}
              onMouseLeave={(e) => {
                if (confirmation !== 'confirmed') {
                  e.currentTarget.style.borderColor = '#f0f0f0';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }
              }}
            >
              <CheckCircleOutlined
                style={{
                  fontSize: '20px',
                  color: '#52c41a',
                  marginBottom: '6px',
                  display: 'block',
                }}
              />
              <Text strong style={{ color: '#262626', fontSize: '14px' }}>
                {t('scheduleConfirm.decision.confirm')}
              </Text>
            </div>
            <div
              onClick={() => setConfirmation('cancelled')}
              style={{
                padding: '12px',
                borderRadius: '6px',
                border:
                  confirmation === 'cancelled'
                    ? '2px solid #ff4d4f'
                    : '2px solid #f0f0f0',
                backgroundColor:
                  confirmation === 'cancelled' ? '#fff1f0' : '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'center',
              }}
              onMouseEnter={(e) => {
                if (confirmation !== 'cancelled') {
                  e.currentTarget.style.borderColor = '#d9d9d9';
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }
              }}
              onMouseLeave={(e) => {
                if (confirmation !== 'cancelled') {
                  e.currentTarget.style.borderColor = '#f0f0f0';
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }
              }}
            >
              <CloseCircleOutlined
                style={{
                  fontSize: '20px',
                  color: '#ff4d4f',
                  marginBottom: '6px',
                  display: 'block',
                }}
              />
              <Text strong style={{ color: '#262626', fontSize: '14px' }}>
                {t('scheduleConfirm.decision.cancel')}
              </Text>
            </div>
          </div>
        </div>

        {confirmation === 'cancelled' && (
          <div
            style={{
              marginBottom: '20px',
              padding: '12px',
              borderRadius: '6px',
              backgroundColor: '#fff1f0',
              border: '1px solid #ffccc7',
            }}
          >
            <Form layout="vertical">
              <Form.Item
                label={
                  <Text strong style={{ fontSize: '14px' }}>
                    {t('scheduleConfirm.decision.cancelReason')}
                  </Text>
                }
                style={{ marginBottom: 0 }}
              >
                <Input.TextArea
                  rows={2}
                  placeholder={t(
                    'scheduleConfirm.decision.cancelReasonPlaceholder',
                  )}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #ffccc7',
                    backgroundColor: '#ffffff',
                    fontSize: '14px',
                  }}
                />
              </Form.Item>
            </Form>
          </div>
        )}

        <Divider style={{ margin: '24px 0' }} />

        <Space
          style={{
            width: '100%',
            justifyContent: 'flex-end',
            marginTop: '20px',
          }}
        >
          <Button
            type="primary"
            size="large"
            onClick={() => setShowConfirmModal(true)}
            loading={loadingConfirm}
            style={{
              borderRadius: '6px',
              height: '40px',
              fontSize: '14px',
              minWidth: '140px',
              backgroundColor:
                confirmation === 'confirmed' ? '#1890ff' : '#ff4d4f',
            }}
          >
            {confirmation === 'confirmed'
              ? t('scheduleConfirm.decision.confirmAction')
              : t('scheduleConfirm.decision.cancelAction')}
          </Button>
        </Space>
      </Card>

      <Modal
        title={
          confirmation === 'confirmed'
            ? `✅ ${t('scheduleConfirm.modal.confirmTitle')}`
            : `❌ ${t('scheduleConfirm.modal.cancelTitle')}`
        }
        open={showConfirmModal}
        onOk={handleSubmitConfirmation}
        onCancel={() => setShowConfirmModal(false)}
        confirmLoading={loadingConfirm}
        okText={
          confirmation === 'confirmed'
            ? t('scheduleConfirm.modal.confirmOk')
            : t('scheduleConfirm.modal.cancelOk')
        }
        cancelText={t('scheduleConfirm.modal.back')}
        okButtonProps={{
          style: {
            backgroundColor:
              confirmation === 'confirmed' ? '#52c41a' : '#ff4d4f',
          },
        }}
        centered
      >
        <div style={{ textAlign: 'center' }}>
          <p
            style={{ fontSize: '16px', color: '#262626', marginBottom: '16px' }}
          >
            {confirmation === 'confirmed'
              ? t('scheduleConfirm.modal.confirmQuestion')
              : t('scheduleConfirm.modal.cancelQuestion')}
          </p>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            {confirmation === 'confirmed'
              ? t('scheduleConfirm.modal.confirmDescription')
              : t('scheduleConfirm.modal.cancelDescription')}
          </Text>
        </div>
      </Modal>
    </ContainerConfirmSchedule>
  );
};

export default ScheduleConfirm;
